import styled, { keyframes, css } from 'styled-components';
import type { SignalsSummary, SignalStatus } from '../types';

export type SignalFilterKey = SignalStatus | 'all' | 'hidden';

interface HealthSummaryBarProps {
  summary: SignalsSummary;
  total: number;
  hiddenCount: number;
  activeFilter: SignalFilterKey;
  onFilterChange: (status: SignalFilterKey) => void;
}

interface StatusChipDef {
  key: SignalStatus | 'all';
  label: string;
  count: (s: SignalsSummary, total: number) => number;
}

// All / Act now / Ready to act / Relax — semantic groupings the
// dashboard uses, each clickable to filter the grid below. Order is
// red→amber→green so the most important state sits closest to the
// "live" indicator on the left.
const CHIPS: StatusChipDef[] = [
  { key: 'all',   label: 'All',          count: (_s, total) => total },
  { key: 'red',   label: 'Act now',      count: (s) => s.red },
  { key: 'amber', label: 'Ready to act', count: (s) => s.amber },
  { key: 'green', label: 'Relax',        count: (s) => s.green }
];

export const HealthSummaryBar = ({
  summary,
  total,
  hiddenCount,
  activeFilter,
  onFilterChange
}: HealthSummaryBarProps) => {
  // The live dot uses the most-severe status as its color so the very first
  // glance at the page tells you whether you should be relaxed (green),
  // attentive (amber), or acting (red).
  const overall: SignalStatus =
    summary.red > 0 ? 'red' : summary.amber > 0 ? 'amber' : 'green';

  return (
    <Bar>
      <Live>
        <LiveDot $status={overall} />
        <LiveLabel>Live</LiveLabel>
      </Live>
      <Divider />
      <Chips role="tablist">
        {CHIPS.map((chip) => {
          const count = chip.count(summary, total);
          const isActive = activeFilter === chip.key;
          return (
            <Chip
              key={chip.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              $status={chip.key}
              $active={isActive}
              onClick={() => onFilterChange(chip.key)}
            >
              <ChipLabel>{chip.label}</ChipLabel>
              <ChipCount $status={chip.key}>{count}</ChipCount>
            </Chip>
          );
        })}
        <Chip
          type="button"
          role="tab"
          aria-selected={activeFilter === 'hidden'}
          $status="hidden"
          $active={activeFilter === 'hidden'}
          onClick={() => onFilterChange('hidden')}
        >
          <ChipLabel>Hidden</ChipLabel>
          <ChipCount $status="hidden">{hiddenCount}</ChipCount>
        </Chip>
      </Chips>
    </Bar>
  );
};

// ── Animations ────────────────────────────────────────────────────────────

const livePulse = keyframes`
  0%   { box-shadow: 0 0 0 0 currentColor; opacity: 0.95; }
  70%  { box-shadow: 0 0 0 6px transparent; opacity: 1; }
  100% { box-shadow: 0 0 0 0 transparent; opacity: 0.95; }
`;

// ── Styled components ─────────────────────────────────────────────────────

const Bar = styled.section`
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.signals.card_bg};
  border: 1px solid ${({ theme }) => theme.signals.card_border};
  border-radius: 14px;
  box-shadow: ${({ theme }) => theme.signals.card_shadow};
`;

const Live = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const LiveDot = styled.span<{ $status: SignalStatus }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme, $status }) => {
    if ($status === 'red') return theme.signals.red_strong;
    if ($status === 'amber') return theme.signals.amber_strong;
    return theme.signals.live_dot;
  }};
  color: ${({ theme, $status }) => {
    if ($status === 'red') return theme.signals.red_strong;
    if ($status === 'amber') return theme.signals.amber_strong;
    return theme.signals.live_dot;
  }};
  animation: ${livePulse} 2.4s ease-out infinite;
`;

const LiveLabel = styled.span`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.signals.text_muted};
`;

const Divider = styled.span`
  width: 1px;
  height: 22px;
  background: ${({ theme }) => theme.signals.divider};
  flex-shrink: 0;
`;

const Chips = styled.div`
  display: flex;
  gap: 8px;
  flex: 1;
  flex-wrap: wrap;
`;

const Chip = styled.button<{ $status: SignalStatus | 'all' | 'hidden'; $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 13px;
  border-radius: 999px;
  border: 1px solid transparent;
  cursor: pointer;
  background: ${({ theme }) => theme.signals.chip_bg};
  color: ${({ theme }) => theme.signals.text_secondary};
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.signals.divider};
  }

  ${({ $active, $status, theme }) =>
    $active &&
    css`
      background: ${theme.signals.card_bg};
      border-color: ${(() => {
        if ($status === 'red') return theme.signals.red_strong;
        if ($status === 'amber') return theme.signals.amber_strong;
        if ($status === 'green') return theme.signals.green_strong;
        if ($status === 'hidden') return theme.signals.text_muted;
        return theme.signals.text_secondary;
      })()};
      color: ${theme.signals.text_primary};
    `}
`;

const ChipLabel = styled.span``;

const ChipCount = styled.span<{ $status: SignalStatus | 'all' | 'hidden' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  padding: 0 6px;
  height: 18px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  background: ${({ theme, $status }) => {
    if ($status === 'red') return theme.signals.red_strong;
    if ($status === 'amber') return theme.signals.amber_strong;
    if ($status === 'green') return theme.signals.green_strong;
    if ($status === 'hidden') return theme.signals.text_muted;
    return theme.signals.text_secondary;
  }};
  color: ${({ theme }) => theme.signals.badge_text};
`;
